import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Drawer, Empty, Grid, Input, Menu, Popconfirm, Select, Skeleton, Space, Table, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, FilterOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import SubmissionStatus, { submissionLabels } from '../components/SubmissionStatus';
import { RegistrationDetails } from '../components/RegistrationDetails';
import { formatDateTime, useRecordView } from '../utils/recordView';

const { Title, Text } = Typography;
const filterKeys = ['search', 'city', 'businessLine', 'employmentType', 'startDate', 'endDate', 'robotName', 'submissionStatus'];
const statusOptions = ['blocked', 'pending', 'accepted', 'unknown', 'rejected', 'legacy'].map(value => ({ value, text: submissionLabels[value] }));

export default function PushRecords() {
  const { user, hasPermission } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const canRead = hasPermission('pushRecords', 'read');
  const screens = Grid.useBreakpoint();
  const view = useRecordView(filterKeys);
  const query = JSON.stringify(view.params);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({});
  const [robots, setRobots] = useState([]);
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState(view.filters.search);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [manualBusyId, setManualBusyId] = useState('');
  const [manualResult, setManualResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setSearch(view.filters.search); }, [view.filters.search]);
  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    setLoading(true); setError(''); setSelected([]);
    api.get('/push-records', { params: JSON.parse(query) }).then(({ data }) => {
      if (!cancelled) { setRecords(data.records); setTotal(data.pagination.total); }
    }).catch(err => { if (!cancelled) setError(err.response?.data?.message || '报名记录加载失败，请重试'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, reload, canRead]);
  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    api.get('/push-records/stats/summary').then(({ data }) => { if (!cancelled) setOptions(data); }).catch(() => {});
    if (isSuperAdmin) api.get('/robot-settings', { params: { limit: 1000, page: 1 } }).then(({ data }) => { if (!cancelled) setRobots(data.robots || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [canRead, isSuperAdmin]);
  useEffect(() => {
    if (!canRead || !view.detailId) { setDetail(null); return; }
    let cancelled = false;
    setDetail(null); setDetailLoading(true); setDetailError('');
    api.get(`/push-records/${encodeURIComponent(view.detailId)}`).then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch(err => { if (!cancelled) setDetailError(err.response?.data?.message || '报名详情加载失败，请重试'); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [canRead, view.detailId, reload]);

  const manualSubmit = async record => {
    if (manualBusyId) return;
    setManualBusyId(record._id); setManualResult(null);
    try {
      const { data } = await api.post(`/push-records/${record._id}/manual-submit`);
      setManualResult({ type: data.recordingError || data.status === 'unknown' ? 'warning' : data.success ? 'success' : 'error',
        message: data.message, description: data.warnings?.join('；') });
    } catch (err) {
      setManualResult({ type: 'warning', message: err.response?.data?.message || '请求未完成，请刷新核对伙伴通接收状态' });
    } finally { setReload(value => value + 1); setManualBusyId(''); }
  };
  const manualAction = (record, compact = false) => {
    if (!isSuperAdmin) return null;
    if (record.submissionStatus !== 'blocked' || !record.canManualSubmit) {
      if (!compact) return null;
      const reason = record.submissionStatus === 'accepted' ? '伙伴通已接收，无需重推' :
        ['pending', 'unknown'].includes(record.submissionStatus) ? '请先核对伙伴通接收结果，避免重复提交' : '当前记录不支持重推，请查看详情核对原因';
      return <Tooltip title={reason}><span><Button type="link" icon={<SendOutlined />} disabled>重推</Button></span></Tooltip>;
    }
    return <Popconfirm title="确认重新推送到伙伴通？"
      description={<div className="confirmation-copy">{record.name} / {record.phone}<br />将重新匹配最新岗位并提交。自动提交关闭时也可执行本次操作。</div>}
      okText="确认重推" cancelText="取消" disabled={Boolean(manualBusyId)} onConfirm={() => manualSubmit(record)}
      okButtonProps={{ loading: manualBusyId === record._id }}>
      <Button type={compact ? 'link' : 'primary'} icon={<SendOutlined />} loading={manualBusyId === record._id}
        disabled={Boolean(manualBusyId) && manualBusyId !== record._id}>重推</Button>
    </Popconfirm>;
  };

  const exportRecords = async format => {
    setExporting(true);
    try {
      const response = await api.get(`/push-records/export/${format}`, { params: view.filters, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.download = `报名记录.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
      message.success('已导出当前筛选结果');
    } catch { message.error('导出失败，请重试'); } finally { setExporting(false); }
  };
  const deleteSelected = async () => {
    setDeleting(true);
    const results = await Promise.allSettled(selected.map(id => api.delete(`/push-records/${id}`)));
    const failures = results.filter(item => item.status === 'rejected').length;
    if (failures) message.warning(`${failures} 条记录删除失败，请刷新核对`); else message.success('所选记录已删除');
    setSelected([]); setReload(value => value + 1); setDeleting(false);
  };
  const statusFilter = {
    filters: statusOptions,
    filterMultiple: false,
    filteredValue: view.filters.submissionStatus ? [view.filters.submissionStatus] : null,
    filterIcon: <FilterOutlined aria-label="筛选接收状态" />,
    filterDropdown: ({ setSelectedKeys, confirm }) => <Menu className="push-status-filter" selectable
      selectedKeys={[view.filters.submissionStatus]} items={[{ key: '', label: '全部状态' }, ...statusOptions.map(option => ({ key: option.value, label: option.text }))]}
      onClick={({ key }) => { setSelectedKeys(key ? [key] : []); confirm({ closeDropdown: true }); }} />
  };
  const columns = [
    { title: screens.sm ? '报名人员' : '报名人员 / 状态', key: 'person', width: 155, ...(!screens.sm ? statusFilter : {}),
      render: (_, record) => <div><Text strong>{record.name}</Text><div className="cell-secondary">{record.phone}</div>
      <div className="mobile-record-summary"><SubmissionStatus status={record.submissionStatus} /><div>{record.submissionMessage || record.site}</div></div></div> },
    { title: '接收状态', dataIndex: 'submissionStatus', width: 170, responsive: ['sm'], ...(screens.sm ? statusFilter : {}), render: status => <SubmissionStatus status={status} /> },
    { title: '意向岗位', key: 'job', width: 200, responsive: ['md'], render: (_, record) => <div><div>{record.site}</div><div className="cell-secondary">{[record.city, record.businessLine, record.employmentType].filter(Boolean).join(' · ')}</div></div> },
    { title: '来源', key: 'source', width: 160, responsive: ['md'], render: (_, record) => <div>{record.senderGroupName || '—'}<div className="cell-secondary">{record.robotName || record.sender || '—'}</div></div> },
    { title: '当前结果 / 待办原因', dataIndex: 'submissionMessage', width: 240, responsive: ['sm'], render: (value, record) => <Text>{value || (record.partnerRecordId ? `伙伴通编号：${record.partnerRecordId}` : '打开详情查看')}</Text> },
    { title: '创建时间', dataIndex: 'createdAt', width: 165, responsive: ['lg'], render: formatDateTime },
    { title: '操作', key: 'actions', width: screens.md ? 200 : 112, fixed: 'right', className: 'table-action-cell',
      render: (_, record) => <Space wrap size={8}><Button type="link" icon={<EyeOutlined />} onClick={() => view.openDetail(record._id)}>查看详情</Button>{manualAction(record, true)}</Space> }
  ];
  const tableWidth = columns.filter(column => !column.responsive || column.responsive.some(breakpoint => screens[breakpoint]))
    .reduce((width, column) => width + column.width, isSuperAdmin ? 32 : 0);
  const extraFilters = ['city', 'businessLine', 'employmentType'];
  const labels = { city: '城市', businessLine: '业务线', employmentType: '用工类型' };
  const optionKeys = { city: 'cities', businessLine: 'businessLines', employmentType: 'employmentTypes' };
  if (!canRead) return <Alert type="warning" showIcon message="没有报名记录查看权限" />;
  return <div>
    <div className="page-header page-header-with-actions"><div><Title level={2}>推送记录</Title>
      <div className="description">从原始报名到岗位匹配、伙伴通接收，在这里查看结果和处理异常。</div></div>
      <Button icon={<ReloadOutlined />} loading={loading} onClick={() => setReload(value => value + 1)}>刷新</Button></div>
    {manualResult && <Alert className="page-alert" {...manualResult} showIcon closable onClose={() => setManualResult(null)} />}
    {error && <Alert className="page-alert" type="error" showIcon message={error} description="列表保留上次结果，请刷新后再操作。" action={<Button onClick={() => setReload(value => value + 1)}>重试</Button>} />}
    <Card>
      <div className="record-toolbar">
        <Input.Search aria-label="搜索报名" placeholder="姓名、手机号、岗位或原文" allowClear value={search} onChange={event => setSearch(event.target.value)} onSearch={value => view.update({ search: value })} className="record-search" />
        <DatePicker.RangePicker aria-label="报名日期" value={view.filters.startDate && view.filters.endDate ? [dayjs(view.filters.startDate), dayjs(view.filters.endDate)] : null}
          onChange={(_, values) => view.update({ startDate: values[0], endDate: values[1] })} />
        <Button onClick={view.clear} disabled={!Object.values(view.filters).some(Boolean)}>清空筛选</Button>
        <div className="toolbar-spacer" />
        {hasPermission('pushRecords', 'export') && <Space wrap><Button icon={<DownloadOutlined />} loading={exporting} onClick={() => exportRecords('excel')}>导出 Excel</Button><Button loading={exporting} onClick={() => exportRecords('csv')}>CSV</Button></Space>}
      </div>
      <div className="push-record-filters">
        {extraFilters.map(key => <Select key={key} aria-label={labels[key]} placeholder={labels[key]} allowClear showSearch optionFilterProp="label" className="filter-select"
          value={view.filters[key] || undefined} onChange={value => view.update({ [key]: value })}
          options={(options[optionKeys[key]] || []).filter(item => item.name).map(item => ({ value: item.name, label: item.name }))} />)}
        {isSuperAdmin && <Select aria-label="接收机器人" placeholder="接收机器人" allowClear className="filter-select" value={view.filters.robotName || undefined}
          onChange={robotName => view.update({ robotName })} options={robots.map(robot => ({ value: robot.name, label: robot.name }))} />}
      </div>
      {selected.length > 0 && <div className="selection-bar"><Text>已选 {selected.length} 条</Text><Popconfirm title={`确定删除选中的 ${selected.length} 条记录？`} okText="删除" cancelText="取消" onConfirm={deleteSelected}><Button danger size="small" icon={<DeleteOutlined />} loading={deleting}>删除所选</Button></Popconfirm></div>}
      <Table rowKey="_id" columns={columns} dataSource={records} loading={loading} scroll={screens.sm ? { x: tableWidth } : undefined}
        rowSelection={isSuperAdmin ? { selectedRowKeys: selected, onChange: setSelected } : undefined}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error ? '记录加载失败，请重试' : '暂无符合条件的报名'} /> }}
        pagination={{ current: view.page, pageSize: view.pageSize, total, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], showTotal: count => `共 ${count} 条报名记录` }}
        onChange={(pagination, filters, _, extra) => extra.action === 'filter'
          ? view.update({ submissionStatus: filters.submissionStatus?.[0] || filters.person?.[0] || '' })
          : view.update({ page: pagination.current, limit: pagination.pageSize }, false)} />
    </Card>
    <Drawer title="报名详情" open={Boolean(view.detailId)} onClose={view.closeDetail} width="min(820px, 100vw)" destroyOnHidden>
      {detailLoading ? <Skeleton active /> : detailError ? <Alert type="error" showIcon message={detailError} action={<Button onClick={() => setReload(value => value + 1)}>重试</Button>} /> : detail && <>
        {manualResult && <Alert className="page-alert" {...manualResult} showIcon />}
        <RegistrationDetails record={detail} action={manualAction(detail)} />
      </>}
    </Drawer>
  </div>;
}
